// PeopleGroup + AppPersonRow used to be redefined here, shadowing the
// canonical versions in packages/shared/components/AppSidebar.js under the
// shell's project-barrel-wins precedence (this file is barrelled at
// components/index.js, shared is merged in first). Removed 2026-07-27 —
// see tickets/workspace.md (AppPersonRow/PeopleGroup duplicate-name
// collision). The single remaining definition (shared/components/
// AppSidebar.js) now carries the union of both: the `|| []` null-safe
// guard + `s.user?.*` wrapped-row fallbacks that used to live only here,
// AND the onClick popover that used to live only in this copy.

// ── Home left rail (HomeSidebar) ────────────────────────────────────────────
// A labelled-list sidebar for the home page: chat channels, members, agents
// and upcoming events. Each row is compact and routes into its app on click.
// Members reuse AppPersonRow (shared/components/AppSidebar.js). Channels
// come from root.channels (loaded by the home page's onCreate); members
// from activeMembers; events from root.intranetEvents (loaded by
// loadPrivateData); agents from the small roster below.

const SIDEBAR_AGENTS = [
  { name: 'Simone', caption: 'workspace.sidebar.agent.simone' },
  { name: 'Temo', caption: 'workspace.sidebar.agent.temo' },
  { name: 'Watcher', caption: 'workspace.sidebar.agent.watcher' },
  { name: 'Caesar', caption: 'workspace.sidebar.agent.caesar' }
]

// Upcoming intranet events (loaded by loadPrivateData), soonest first.
const _sidebarEvents = (s) => {
  const now = Date.now()
  return (s.root.intranetEvents || [])
    .filter((e) => !e.startAt || new Date(e.startAt).getTime() >= now - 36e5)
    .sort((a, b) => new Date(a.startAt || 0) - new Date(b.startAt || 0))
    .slice(0, 6)
}

// Labelled group = caption title + a childExtends list. `list` supplies
// childExtends + children; childrenAs:'state' is fixed.
const _sidebarGroup = (label, list) => ({
  flow: 'y',
  gap: 'Z',
  CaptionTitle: { Text: { text: label } },
  List: { flow: 'y', gap: 'V', childrenAs: 'state', ...list }
})

// Measure the app rail ([data-app-rail]) so the chat drawer can dock flush
// beside it as an inset card. Returns the dock rect (stashed in state, so the
// ChatDrawer slot's reactive position factories survive its re-renders), or
// null when no rail is on screen. GAP ≈ the rail's 'Z' inter-card gap.
const _measureDock = (el) => {
  const GAP = 8
  const rail =
    el && el.node && el.node.ownerDocument && el.node.ownerDocument.querySelector('[data-app-rail]')
  if (!rail) return null
  const r = rail.getBoundingClientRect()
  return {
    left: Math.round(r.right + GAP),
    top: Math.round(r.top + GAP),
    height: Math.round(r.height - GAP * 2)
  }
}

export const HomeSidebar = {
  // Same chrome as the /chat, /tickets, /calendar … rails: extends the shared
  // base AppSidebar (width G, full-height, childExtends CommonCard). Each
  // section is a direct child, so it renders as its own CommonCard with a
  // CaptionTitle header — sections read as cards separated by the rail gap.
  // (Must NOT be named AppSidebar — that name is the shared base every other
  // rail extends; shadowing it breaks chat/tickets/calendar/meet/data/admin.)
  extends: 'AppSidebar',
  overflow: 'auto',
  gap: 'A',
  // Marker so the ChatDrawer can dock itself flush to this rail's right edge
  // (see _alignToRail in chat/collab/drawer.js).
  attr: { 'data-app-rail': 'true' },

  // Unified chat card: CHANNELS up top, then a DIRECT MESSAGES sub-caption
  // (reuses /chat's CaptionTitle section-header style) over the people. Clicking
  // a channel opens its conversation; clicking a person opens a 1:1 DM — both in
  // the chat window docked beside the rail. One card (unified) with the two
  // /chat-style captions inside.
  ChatGroup: {
    flow: 'y',
    gap: 'Z',
    CaptionTitle: { Text: { text: '{{ workspace.sidebar.channels | polyglot }}' } },
    ChannelsList: {
      flow: 'y',
      gap: 'V',
      childrenAs: 'state',
      childExtends: 'SidebarChatRow',
      children: (el, s) =>
        (s.root.channels || []).filter((c) => c.name && c.kind !== 'dm').slice(0, 10)
    },
    // Reuse the same CaptionTitle as the /chat sidebar's section headers.
    DmCaption: {
      extends: 'CaptionTitle',
      marginTop: 'Z',
      hide: (el) => !(el.call('activeMembers') || []).length,
      Text: { text: '{{ workspace.sidebar.directMessages | polyglot }}' }
    },
    MembersList: {
      flow: 'y',
      gap: 'V',
      childrenAs: 'state',
      childExtends: 'SidebarChatRow',
      children: (el, s) => (el.call('activeMembers') || []).slice(0, 10)
    }
  },

  AgentsGroup: _sidebarGroup('AGENTS', {
    childExtends: 'SidebarAgentRow',
    children: () => SIDEBAR_AGENTS
  }),

  EventsGroup: _sidebarGroup('EVENTS', {
    childExtends: 'SidebarEventRow',
    children: (el, s) => _sidebarEvents(s)
  })
}

// Shared compact-row chrome — a borderless, hover-fill button.
const _sidebarRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  width: '100%',
  padding: 'V Z',
  round: 'radiusControl',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { background: 'hover' }
}

// Unified CHAT row — renders a channel (# glyph) or a person (avatar) and
// opens the chat window (ChatDrawer, mounted in workspace/app.js) docked beside
// the rail — no redirect. Channel → its conversation; person → a 1:1 DM
// (messageUser finds/creates the dm and sets activeChannelId). The leading slot
// is a fixed 'A1' box either way so channel and person rows align.
export const SidebarChatRow = {
  ..._sidebarRow,
  onClick: (e, el, s) => {
    e.stopPropagation()
    const dock = _measureDock(el)
    if (s && s.kind === 'channel') {
      s.root.update({ _chatDrawerOpen: true, activeChannelId: s.id, _chatDockRect: dock })
      // Bundle-fold: the overlay mounts once the fold latch flips (reactive
      // `if:` in app.js) — kick the fold so the flag above takes effect.
      Promise.resolve(el.call('ensureChatContext')).catch(() => {})
    } else if (s && s.email) {
      // Open the drawer now; messageUser resolves/creates the DM and sets
      // activeChannelId, which the open drawer then loads. messageUser lives
      // in the lazily-folded chat context — sequence it behind the fold.
      s.root.update({ _chatDrawerOpen: true, _chatDockRect: dock })
      Promise.resolve(el.call('ensureChatContext'))
        .then((ok) => {
          if (ok !== false) el.call('messageUser', s.email)
        })
        .catch(() => {})
    }
  },
  Hash: {
    tag: 'span',
    display: (el, s) => (s && s.kind === 'channel' ? 'flex' : 'none'),
    boxSize: 'A1 A1',
    flexShrink: '0',
    align: 'center center',
    color: 'caption',
    fontSize: 'Z1',
    text: '#'
  },
  Avatar: {
    display: (el, s) => (s && s.kind === 'channel' ? 'none' : 'flex'),
    avatarType: 'initials',
    boxSize: 'A1 A1',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', s && s.name)
  },
  Name: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z1',
    color: (el, s) => (s && s.kind === 'channel' ? 'paragraph' : 'title'),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && (s.name || (s.kind === 'channel' ? 'channel' : ''))) || ''
  }
}

export const SidebarAgentRow = {
  ..._sidebarRow,
  onClick: (e, el) => el.router('/team-and-agents', el.getRoot()),
  Glyph: { tag: 'span', color: 'caption', fontSize: 'Z1', text: '◆' },
  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',
    Name: {
      tag: 'span',
      fontSize: 'Z1',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name
    },
    Caption: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('polyglot', s.caption || '')
    }
  }
}

export const SidebarEventRow = {
  ..._sidebarRow,
  // Open the event detail modal in place (CalEventDetailModal, mounted in
  // workspace/app.js) instead of redirecting to /calendar. Mirrors the
  // calendar Week/Month views, which set selectedEvent + showEventDetail.
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (!s) return
    el.call('openEventDetailModal', {
      id: s.id,
      title: s.title,
      startAt: s.startAt,
      endAt: s.endAt,
      // intranetEvents carry the guest list in `attendees` (r.invitees) and a
      // description snippet in `location`; the detail modal reads `invitees` +
      // `description`. Map them so guests render immediately instead of
      // stalling on fetchEventDetails (no calendar-page context on /).
      invitees: Array.isArray(s.attendees) ? s.attendees : [],
      creatorEmail: s.organizerEmail,
      description: s.location || ''
    })
  },
  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',
    Title: {
      tag: 'span',
      fontSize: 'Z1',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.title || s.name || el.call('polyglot', 'workspace.sidebar.event.untitled')
    },
    When: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => {
        if (!s.startAt) return ''
        const d = new Date(s.startAt)
        if (Number.isNaN(d.getTime())) return ''
        return (
          el.call('localizedDate', d, { month: 'short', day: 'numeric' }) +
          ' · ' +
          el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' })
        )
      }
    }
  }
}
